import { useState, useEffect, useCallback } from 'preact/hooks'
import { apiClient, InitData, tenantSiteUrl, assetUrl, udLink } from '../../core'
import { t, intlLocale, formatMoneyParts } from '../../core/i18n'
// Retreatens tidsrum i studiets tidszone, ikke den besøgendes browser (QA 27-10).
import { fmtDatoInterval, studietsTidszone } from '../../core/tidszone'

interface RetreatListItem {
  id: string
  title: string
  slug: string
  /** 'event' for typen event (knaptekst «Se event»); mangler på ældre svar = retreat. */
  kind?: 'retreat' | 'event'
  shortDescription: string | null
  heroImageUrl: string | null
  startsAt: string
  endsAt: string
  locationName: string | null
  currency: string
  fromPrice: number | null
  availableSpots: number | null
  soldOut: boolean
  /** Tilmeldingsfristen er passeret (sat af serveren). */
  registrationClosed?: boolean
  landingPath: string | null
}

interface Props {
  initData: InitData
  /** Kompakt visning: mindre kort, så 8-12 events står pr. side. Default: false. */
  compact?: boolean
  /** Antal pr. side med «Vis flere». Default: alle (kompakt: KOMPAKT_SIDESTOERRELSE). */
  pageSize?: number | null
}

/** Kompakt visning viser som standard så mange kort ad gangen (8-12 pr. side). */
export const KOMPAKT_SIDESTOERRELSE = 12

/** Svar fra /api/widget/retreats med sideopdeling (?page&pageSize). */
interface SideSvar {
  items: RetreatListItem[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

/** Antal pr. side, eller null når alle hentes i ét kald (standarden, uændret). */
export function sidestoerrelse(compact: boolean, pageSize: number | null | undefined): number | null {
  return pageSize ?? (compact ? KOMPAKT_SIDESTOERRELSE : null)
}

/**
 * RetreatsWidget — overview grid of upcoming retreats, mirroring the
 * SeasonsWidget card styling. Each card links to the retreat's landing page on
 * the tenant's own site, where the battle-tested booking modal (Stripe, gift
 * cards, package-travel consent) handles the transaction — the same way the
 * seasons/membership widgets punt payment back to the tenant site.
 */
export function RetreatsWidget({ initData, compact = false, pageSize = null }: Props) {
  const str = t(initData.locale)
  const dateLocale = intlLocale(initData.locale)
  const [retreats, setRetreats] = useState<RetreatListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [side, setSide] = useState(1)
  const [flere, setFlere] = useState(false)
  const [henterFlere, setHenterFlere] = useState(false)
  const pr = sidestoerrelse(compact, pageSize)

  // Sideopdelt (kompakt / page-size): serveren sender én side ad gangen. Ellers hentes alle som før.
  const fetchSide = useCallback(async (nr: number): Promise<RetreatListItem[] | null> => {
    const result = await apiClient.get<RetreatListItem[] | SideSvar>(
      pr === null ? '/api/widget/retreats' : `/api/widget/retreats?page=${nr}&pageSize=${pr}`,
    )
    if (result.error) {
      setError(result.error)
      return null
    }
    const data = result.data
    if (!data) return null
    if (Array.isArray(data)) {
      // Ældre svar uden sideopdeling: alt er hentet.
      setFlere(false)
      return data
    }
    setFlere(data.hasMore)
    return data.items
  }, [pr])

  useEffect(() => {
    let aktiv = true
    fetchSide(1).then(items => {
      if (!aktiv) return
      if (items) setRetreats(items)
      setLoading(false)
    })
    return () => { aktiv = false }
  }, [fetchSide])

  const visFlere = async () => {
    setHenterFlere(true)
    const items = await fetchSide(side + 1)
    if (items) {
      setRetreats(prev => [...prev, ...items.filter(i => !prev.some(p => p.id === i.id))])
      setSide(side + 1)
    }
    setHenterFlere(false)
  }

  if (loading) {
    return (
      <div>
        <style>{styles}</style>
        <div class={`hsw-retreat-grid${compact ? ' hsw-compact' : ''}`}>
          {[0, 1, 2].map(i => (
            <div key={i} class="hsw-retreat-card">
              <div class="hsw-skel hsw-skel-img" />
              <div class="hsw-retreat-body">
                <div class="hsw-skel hsw-skel-label" />
                <div class="hsw-skel hsw-skel-title" />
                <div class="hsw-skel hsw-skel-line" />
                <div class="hsw-skel hsw-skel-line" style={{ width: '85%' }} />
                <div class="hsw-skel hsw-skel-button" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{styles}</style>
        <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
          <strong>⚠️ {error}</strong>
        </div>
      </div>
    )
  }

  if (!retreats.length) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🌿</div>
        {/* Neutral: en tom liste afslører ikke, om studiet har retreats eller events (fund RetreatsWidget.tsx:147). */}
        <p>{str.noUpcoming}</p>
      </div>
    )
  }

  const siteUrl = tenantSiteUrl(initData)

  return (
    <div>
      <style>{styles}</style>
      <div class={`hsw-retreat-grid${compact ? ' hsw-compact' : ''}`}>
        {retreats.map(retreat => {
          const priceParts = retreat.fromPrice !== null
            // Price is in the RETREAT's own currency (each retreat carries it), not the
            // tenant's init currency — otherwise a EUR retreat on a DKK tenant renders
            // "fra DKK 2.500" while the detail widget correctly shows EUR.
            ? formatMoneyParts(retreat.fromPrice, initData.locale, retreat.currency)
            : null
          const initial = retreat.title.charAt(0).toUpperCase()
          const href = retreat.landingPath ? `${siteUrl}${retreat.landingPath}` : null

          return (
            <div key={retreat.id} class={`hsw-retreat-card${retreat.soldOut || retreat.registrationClosed ? ' hsw-closed' : ''}`}>
              {retreat.heroImageUrl ? (
                <img src={assetUrl(retreat.heroImageUrl)} alt={retreat.title} class="hsw-retreat-img" loading="lazy" />
              ) : (
                <div class="hsw-retreat-img hsw-retreat-img-fallback">
                  <span aria-hidden="true">{initial}</span>
                </div>
              )}
              <div class="hsw-retreat-body">
                <div class="hsw-retreat-dates">
                  {fmtDatoInterval(retreat.startsAt, retreat.endsAt, dateLocale, studietsTidszone(initData.timezone))}
                </div>
                <h3 class="hsw-retreat-name">{retreat.title}</h3>
                {retreat.locationName && (
                  <div class="hsw-retreat-location">📍 {retreat.locationName}</div>
                )}
                {retreat.shortDescription && (
                  <p class="hsw-retreat-desc">{retreat.shortDescription}</p>
                )}

                <div class="hsw-retreat-meta">
                  {priceParts !== null ? (
                    <span class="hsw-retreat-price">
                      <span class="hsw-retreat-price-label">{str.fromPrice}</span>
                      {priceParts.symbolFirst && <span class="hsw-retreat-price-suffix">{priceParts.symbol}</span>}
                      <span class="hsw-retreat-price-amount">{priceParts.amount}</span>
                      {!priceParts.symbolFirst && <span class="hsw-retreat-price-suffix">{priceParts.symbol}</span>}
                    </span>
                  ) : <span />}
                  {retreat.registrationClosed ? (
                    <span class="hsw-retreat-spots hsw-full">{str.registrationClosed}</span>
                  ) : retreat.soldOut ? (
                    <span class="hsw-retreat-spots hsw-full">{str.soldOut}</span>
                  ) : retreat.availableSpots !== null && retreat.availableSpots > 0 ? (
                    <span class="hsw-retreat-spots hsw-open">
                      {retreat.availableSpots} {retreat.availableSpots === 1 ? str.spotAvailable : str.spotsAvailable}
                    </span>
                  ) : <span />}
                </div>

                {href ? (
                  <a href={href} {...udLink(href)} class="hsw-retreat-cta">{retreat.kind === 'event' ? str.viewEvent : str.viewRetreat}</a>
                ) : (
                  <span class="hsw-retreat-cta hsw-retreat-cta-static">{retreat.kind === 'event' ? str.viewEvent : str.viewRetreat}</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {flere && (
        <div class="hsw-retreat-more">
          <button type="button" class="hsw-retreat-more-btn" onClick={visFlere} disabled={henterFlere}>{str.showMore}</button>
        </div>
      )}
    </div>
  )
}

const styles = `
  .hsw-retreat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  .hsw-retreat-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all .3s;
  }
  .hsw-retreat-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  .hsw-retreat-card.hsw-closed { opacity: 0.75; }
  .hsw-retreat-img {
    display: block;
    width: 100%;
    height: 200px;
    object-fit: cover;
  }
  .hsw-retreat-img-fallback {
    background: linear-gradient(135deg, var(--yoga-primary, #FF6B35), var(--yoga-secondary, var(--yoga-primary-900, #1A1A2E)));
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 4rem;
    font-weight: 800;
    letter-spacing: -0.04em;
  }
  .hsw-retreat-body {
    padding: 1.75rem 1.5rem;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .hsw-retreat-dates {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--yoga-primary, #FF6B35);
    margin-bottom: 0.6rem;
  }
  .hsw-retreat-name {
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0 0 0.4rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
    line-height: 1.25;
  }
  .hsw-retreat-location {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
    margin-bottom: 0.6rem;
  }
  .hsw-retreat-desc {
    color: var(--yoga-text-muted, #525252);
    font-size: 0.9rem;
    line-height: 1.55;
    margin: 0 0 1.25rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hsw-retreat-meta {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.75rem;
    margin: auto 0 1.25rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--yoga-card-border, #e5e7eb);
    flex-wrap: wrap;
  }
  .hsw-retreat-price {
    display: inline-flex;
    align-items: baseline;
    gap: 0.25rem;
    color: var(--yoga-text, #0a0a0a);
  }
  .hsw-retreat-price-label {
    font-size: 0.75rem;
    color: var(--yoga-text-muted, #737373);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 600;
  }
  .hsw-retreat-price-amount {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.02em;
  }
  .hsw-retreat-price-suffix {
    font-size: 0.8rem;
    color: var(--yoga-text-muted, #737373);
    font-weight: 500;
  }
  .hsw-retreat-spots {
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-retreat-spots.hsw-open { color: #15803d; }
  .hsw-retreat-spots.hsw-full { color: #b91c1c; }
  .hsw-retreat-cta {
    display: block;
    margin-top: auto;
    padding: 0.85rem 1.25rem;
    text-align: center;
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    font-weight: 600;
    border: 1px solid var(--yoga-primary, #FF6B35);
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-size: 0.9rem;
    letter-spacing: 0.01em;
    transition: all .3s;
  }
  .hsw-retreat-cta:hover { filter: brightness(0.94); }
  .hsw-retreat-cta-static {
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    border-color: #d4d4d4;
    cursor: default;
  }
  /* Skeletons */
  .hsw-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    border-radius: 4px;
  }
  .hsw-skel-img { width: 100%; height: 200px; border-radius: 0; }
  .hsw-skel-label { height: 12px; width: 50%; margin-bottom: 0.75rem; }
  .hsw-skel-title { height: 22px; width: 75%; margin-bottom: 1rem; }
  .hsw-skel-line { height: 14px; width: 100%; margin-bottom: 0.55rem; }
  .hsw-skel-button { height: 44px; width: 100%; margin-top: 1.5rem; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @media (max-width: 600px) {
    .hsw-retreat-img,
    .hsw-skel-img { height: 160px; }
    .hsw-retreat-body { padding: 1.25rem 1.1rem; }
  }
  /* Kompakt visning: 4-5 kort pr. række, så 8-12 events står pr. side */
  .hsw-retreat-grid.hsw-compact {
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: 1rem;
  }
  .hsw-compact .hsw-retreat-img,
  .hsw-compact .hsw-skel-img { height: 110px; }
  .hsw-compact .hsw-retreat-img-fallback { font-size: 2rem; }
  .hsw-compact .hsw-retreat-body { padding: 0.85rem 0.9rem; }
  .hsw-compact .hsw-retreat-dates { font-size: 0.68rem; letter-spacing: 0.08em; margin-bottom: 0.3rem; }
  .hsw-compact .hsw-retreat-name {
    font-size: 1rem;
    margin-bottom: 0.25rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hsw-compact .hsw-retreat-location {
    font-size: 0.78rem;
    margin-bottom: 0.4rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .hsw-compact .hsw-retreat-desc { display: none; }
  .hsw-compact .hsw-retreat-meta { margin: auto 0 0.6rem; padding-top: 0.5rem; gap: 0.4rem; }
  .hsw-compact .hsw-retreat-price-label { font-size: 0.65rem; }
  .hsw-compact .hsw-retreat-price-amount { font-size: 1.1rem; }
  .hsw-compact .hsw-retreat-spots { font-size: 0.7rem; }
  .hsw-compact .hsw-retreat-cta { padding: 0.5rem 0.75rem; font-size: 0.8rem; }
  .hsw-retreat-more { text-align: center; margin: 1.5rem 0 0; }
  .hsw-retreat-more-btn {
    padding: 0.7rem 1.75rem;
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 0);
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
  }
  .hsw-retreat-more-btn:disabled { opacity: 0.6; cursor: default; }
  .hsw-retreat-more-btn:hover { border-color: var(--yoga-primary, #FF6B35); color: var(--yoga-primary, #FF6B35); }
  @media (max-width: 600px) {
    .hsw-retreat-grid.hsw-compact { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
    .hsw-compact .hsw-retreat-img,
    .hsw-compact .hsw-skel-img { height: 90px; }
    .hsw-compact .hsw-retreat-body { padding: 0.65rem 0.7rem; }
  }
`
